import * as React from 'react';
import {
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import {
  loadedSelectionHeaderState,
  manageDialogFetchSize,
  mergeLoadedSelection,
  pageClientRows,
} from '../../../../../lib/clientSelectionList';
import { useTargetingTailoredSupplyQuery } from '../../../../supply/tailored-supply/hooks';
import {
  defaultTailoredSupplyLookupFilter,
  tailoredSupplyPageSizes,
  type TailoredSupplyItem,
} from '../../../../supply/tailored-supply/model';

const lookupFilter = {
  ...defaultTailoredSupplyLookupFilter,
  searchTerm: '',
  page: 0,
  size: manageDialogFetchSize,
};

export function ManageTailoredSupplyDialogContent({
  onCancel,
  onConfirm,
  selectedItems,
}: {
  onCancel: () => void;
  onConfirm: (items: TailoredSupplyItem[]) => void;
  selectedItems: TailoredSupplyItem[];
}) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(5);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [draft, setDraft] = React.useState(selectedItems);
  const selectedIds = React.useMemo(() => new Set(draft.map((item) => item.id)), [draft]);
  const query = useTargetingTailoredSupplyQuery(lookupFilter);
  const paged = pageClientRows({
    items: query.data?.content ?? [],
    searchTerm,
    searchText: (item) => item.name,
    page,
    pageSize,
  });
  const headerState = loadedSelectionHeaderState(
    selectedIds,
    paged.filteredItems.map((item) => item.id),
  );
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<TailoredSupplyItem>([
        {
          id: 'selected',
          header: '',
          kind: DashboardDataTableColumnKind.Selection,
          checked: (item) => selectedIds.has(item.id),
          label: (item) => item.name,
          headerChecked: headerState.checked,
          headerDisabled: headerState.disabled,
          headerLabel: dashboardT('common.selectAll'),
          onHeaderChange: (checked) => {
            setDraft((current) =>
              mergeLoadedSelection(
                current,
                paged.filteredItems,
                checked,
                (item) => item.id,
                (item) => item.id,
                (item) => item,
              ),
            );
          },
          onChange: (item, checked) => {
            setDraft((current) =>
              checked
                ? [...current.filter((candidate) => candidate.id !== item.id), item]
                : current.filter((candidate) => candidate.id !== item.id),
            );
          },
        },
        {
          id: 'name',
          header: dashboardT('supply.name'),
          kind: DashboardDataTableColumnKind.Text,
          value: (item) => item.name,
        },
      ]),
    [headerState.checked, headerState.disabled, paged.filteredItems, selectedIds],
  );
  return (
    <DashboardTableDialogContent>
      <DashboardTableDialogHeader
        title={dashboardT('targeting.tailoredSupply')}
        description={dashboardT('madBuy.supply.tailored.manageDescription')}
      />
      <DashboardTableDialogBody>
        <DashboardTableDialogDataTable
          tableLabel={dashboardT('targeting.tailoredSupply')}
          items={paged.items}
          columns={columns}
          getRowKey={(item) => item.id}
          loading={query.isLoading}
          errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
          emptyMessage={dashboardT('supply.emptySelection')}
          toolbar={
            <DashboardTableSearchInput
              aria-label={dashboardT('supply.search')}
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setPage(0);
              }}
            />
          }
          pagination={{
            value: {
              page: paged.page,
              pageSize,
              totalItems: paged.totalItems,
            },
            pageSizes: tailoredSupplyPageSizes,
            disabled: query.isLoading,
            onChange: (next) => {
              setPage(next.page);
              setPageSize(next.pageSize);
            },
          }}
        />
      </DashboardTableDialogBody>
      <DashboardTableDialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {dashboardT('supply.cancel')}
        </Button>
        <Button
          type="button"
          onClick={() => {
            onConfirm(draft);
          }}
        >
          {dashboardT('supply.select')}
        </Button>
      </DashboardTableDialogFooter>
    </DashboardTableDialogContent>
  );
}
